import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, FileSpreadsheet, Filter } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const today = new Date().toISOString().slice(0, 10);
const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

const PAYMENT_MODES = [
  { value: "_all", label: "All modes" },
  { value: "cash", label: "Cash" },
  { value: "gcash", label: "GCash" },
  { value: "maya", label: "Maya" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "on_account", label: "On Account" },
  { value: "cash_on_pickup", label: "Cash on Pickup" },
  { value: "cash_on_delivery", label: "Cash on Delivery" },
];
const PAYMENT_STATUSES = [
  { value: "_all", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
];
const SOURCES = [
  { value: "_all", label: "All sources" },
  { value: "online", label: "Online" },
  { value: "manual", label: "Walk-in / Manual" },
];

const ReportsPage = () => {
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [paymentMode, setPaymentMode] = useState("_all");
  const [paymentStatus, setPaymentStatus] = useState("_all");
  const [source, setSource] = useState("_all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (paymentMode !== "_all") p.append("payment_mode", paymentMode);
    if (paymentStatus !== "_all") p.append("payment_status", paymentStatus);
    if (source !== "_all") p.append("source", source);
    return p.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/sales?${buildQuery()}`);
      setRows(data.rows); setTotal(data.total_amount);
    } catch (e) { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/reports/sales/export?${buildQuery()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `sales-report-${dateFrom}-to-${dateTo}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 lg:p-10 pav-fade-in max-w-[1400px]" data-testid="reports-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Reports</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Sales reports</h1>
        <p className="text-sm text-neutral-500 mt-1">Filter by date, payment mode, and status. Export to Excel for accounting.</p>
      </div>

      <div className="pav-card mb-6" data-testid="report-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">From</label>
            <input type="date" className="pav-input mt-1" data-testid="filter-date-from"
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">To</label>
            <input type="date" className="pav-input mt-1" data-testid="filter-date-to"
              value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Payment mode</label>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger className="pav-input mt-1" data-testid="filter-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Payment status</label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger className="pav-input mt-1" data-testid="filter-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_STATUSES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Source</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="pav-input mt-1" data-testid="filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={run} disabled={loading} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="run-report-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />} Apply filters
          </button>
          <button onClick={exportExcel} disabled={exporting || rows.length === 0}
            className="btn-accent text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="export-excel-button">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export to Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="pav-card" data-testid="stat-count">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Orders</p>
          <p className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-2">{rows.length}</p>
        </div>
        <div className="pav-card lg:col-span-3" data-testid="stat-total">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Total revenue</p>
          <p className="font-display text-3xl font-extrabold text-[#06B6D4] mt-2">{peso(total)}</p>
        </div>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <p className="p-12 text-center text-neutral-500">No orders match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Fulfillment</th>
                  <th className="text-left px-3 py-2">Payment</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {rows.map((o) => (
                  <tr key={o.order_id} data-testid={`report-row-${o.order_id}`}>
                    <td className="px-3 py-2 text-neutral-600">{(o.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 font-semibold">{o.order_id}</td>
                    <td className="px-3 py-2">{o.customer_name}</td>
                    <td className="px-3 py-2 capitalize text-neutral-600">{o.source}</td>
                    <td className="px-3 py-2 capitalize text-neutral-600">{o.fulfillment_type}</td>
                    <td className="px-3 py-2 capitalize">
                      {(o.payment_mode || "—").replace("_", " ")}
                      {o.payment_status && o.payment_status !== "paid" && <span className="ml-1 text-red-600 text-[10px] uppercase">{o.payment_status}</span>}
                    </td>
                    <td className="px-3 py-2"><span className={`pav-chip status-${o.status}`}>{o.status?.replace(/_/g, " ")}</span></td>
                    <td className="px-3 py-2 text-right font-bold">{peso(o.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#F4F6FB] border-t-2 border-[#0B1E3F]">
                  <td colSpan="7" className="px-3 py-3 text-right font-display font-extrabold">TOTAL</td>
                  <td className="px-3 py-3 text-right font-display font-extrabold text-lg text-[#0B1E3F]">{peso(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
